import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sprout, Heart, HandHeart, Droplets, Users, Flag } from 'lucide-react';
interface ProgramCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  link: string;
}
function ProgramCard({
  title,
  description,
  icon,
  color,
  link
}: ProgramCardProps) {
  return <Link to={link} className="group bg-white rounded-2xl p-8 shadow-soft hover:shadow-hover transition-all duration-300 hover:-translate-y-1 flex flex-col h-full border border-neutral-100" data-id="element-286">
      <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-transform duration-300 ${color} group-hover:scale-110`} data-id="element-287">
        {icon}
      </div>
      <h3 className="text-2xl font-serif font-bold text-charcoal mb-3 group-hover:text-primary transition-colors" data-id="element-288">
        {title}
      </h3>
      <p className="text-neutral-600 mb-6 flex-grow leading-relaxed" data-id="element-289">
        {description}
      </p>
      <div className="flex items-center text-sm font-bold text-charcoal group-hover:text-primary transition-colors" data-id="element-290">
        Learn More{' '}
        <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-291" />
      </div>
    </Link>;
}
export function ProgramsSection() {
  const programs = [{
    title: 'Agriculture & Food Security',
    description: 'Promoting agricultural productivity to combat hunger, improve livelihoods, and reduce poverty in underserved communities.',
    icon: <Sprout className="text-white w-7 h-7" data-id="element-292" />,
    color: 'bg-primary',
    link: '/programs#agriculture'
  }, {
    title: 'Healthcare Access',
    description: 'Supporting healthcare delivery for pregnant women, children, and vulnerable adults, with a focus on prevention, care, and wellbeing.',
    icon: <Heart className="text-white w-7 h-7" data-id="element-293" />,
    color: 'bg-accent',
    link: '/programs#healthcare'
  }, {
    title: 'Humanitarian Services',
    description: 'Responding to urgent needs through relief interventions that improve the welfare of individuals and communities in crisis.',
    icon: <HandHeart className="text-white w-7 h-7" data-id="element-294" />,
    color: 'bg-secondary',
    link: '/programs#humanitarian'
  }, {
    title: 'Clean Water & Sanitation',
    description: 'Providing access to clean, safe, and sustainable water in communities where basic water infrastructure is lacking.',
    icon: <Droplets className="text-white w-7 h-7" data-id="element-295" />,
    color: 'bg-primary',
    link: '/programs#water'
  }, {
    title: 'Social Empowerment',
    description: 'Empowering the less privileged, including orphans, widows, and individuals affected by conflict or natural disasters.',
    icon: <Users className="text-white w-7 h-7" data-id="element-296" />,
    color: 'bg-accent',
    link: '/programs#empowerment'
  }, {
    title: 'Nation Building',
    description: 'Contributing to national development by supporting initiatives that strengthen communities and promote social cohesion.',
    icon: <Flag className="text-white w-7 h-7" data-id="element-297" />,
    color: 'bg-secondary',
    link: '/programs#nation-building'
  }];
  return <section className="py-24 bg-white" data-id="element-298">
      <div className="container mx-auto px-4" data-id="element-299">
        {/* Header with Image Collage */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20 items-center" data-id="element-300">
          <div data-id="element-301">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4" data-id="element-302">
              Our Focus Areas
            </span>
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-charcoal mb-6 leading-tight" data-id="element-303">
              Creating Pathways to Dignity & Opportunity
            </h2>
            <p className="text-neutral-600 text-lg leading-relaxed mb-8" data-id="element-304">
              Our comprehensive approach addresses critical social challenges
              through targeted programs in six key sectors. We believe in
              sustainable solutions that empower communities to thrive with
              dignity and long-term resilience.
            </p>
            <Link to="/programs" className="inline-flex items-center font-bold text-charcoal hover:text-primary transition-colors group" data-id="element-305">
              View All Programs{' '}
              <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-306" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4 h-[400px]" data-id="element-307">
            <div className="col-span-1 h-full rounded-2xl overflow-hidden shadow-soft transform translate-y-8" data-id="element-308">
              <img src="https://cdn.magicpatterns.com/uploads/p3pVnsessfovnWYR2yoJzV/f6651de8-d8ff-4bdf-a83b-efe29d494201.jpg" alt="Community development and training programs" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" width="400" height="400" data-id="element-309" />
            </div>
            <div className="col-span-1 h-full rounded-2xl overflow-hidden shadow-soft" data-id="element-310">
              <img src="https://cdn.magicpatterns.com/uploads/6scM7hFcDkDU7XnaRL8N8f/c89f7068-167c-41a3-b616-1bca527b3956.jpg" alt="Healthcare and empowerment initiatives" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" width="400" height="400" data-id="element-311" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-312">
          {programs.map((program, index) => <div key={index} className="animate-fadeInUp" style={{
          animationDelay: `${index * 100}ms`
        }} data-id="element-313">
              <ProgramCard {...program} data-id="element-314" />
            </div>)}
        </div>
      </div>
    </section>;
}